"use client";

import { Beaker, Droplets, Gem, Sprout, ShieldCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheRmaAdvantage() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLUListElement>();
  const rituals = [{
    icon: Beaker,
    title: "Qualified Lead Acquisition",
    description: "We identify and verify high-intent insurance prospects, ensuring your pipeline is filled with ready-to-buy individuals.",
    caption: "SYSTEM 01 / CONTINUOUS"
  }, {
    icon: Droplets,
    title: "Precision Appointment Setting",
    description: "Our team manages your calendar, securing confirmed meetings with prospects so you can focus on closing deals.",
    caption: "SYSTEM 02 / DAILY"
  }, {
    icon: Gem,
    title: "Performance-Based Outreach",
    description: "We engage your dormant leads and current database to uncover hidden revenue opportunities without upfront costs.",
    caption: "SYSTEM 03 / QUARTERLY"
  }, {
    icon: Sprout,
    title: "Strategic Sales Support",
    description: "From initial contact to final follow-up, we provide the infrastructure to scale your agency's output efficiently.",
    caption: "SYSTEM 04 / SCALABLE"
  }];
  return <section data-section-id="2144" id="the-rma-advantage" className="relative overflow-hidden border-t border-border bg-background py-24 text-foreground md:py-32">
      {/* Background Watermark */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-8 flex justify-center select-none">
        <span className="font-serif font-extralight leading-none text-foreground/[0.03]" style={{
        fontSize: "clamp(12rem, 22vw, 22rem)"
      }}>
          05
        </span>
      </div>

      <div className="relative mx-auto w-full max-w-[1440px] px-6 sm:px-8 md:px-12 lg:px-16">
        {/* Intro Heading Section */}
        <div ref={introRef} className={`mx-auto max-w-[880px] text-center transition-all duration-[640ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${introVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <div className="inline-flex items-center gap-2.5 rounded-lg border border-border bg-secondary/80 px-3.5 py-1.5 shadow-2xs">
            <span className="block h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
            <p className="font-mono text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
              § N° 05 — The RMA Advantage
            </p>
          </div>

          <h2 className="mt-6 font-bold text-foreground" style={{
          fontSize: "clamp(2.25rem, 4vw, 3.75rem)",
          lineHeight: 1.12,
          letterSpacing: "-0.03em",
          fontFamily: "var(--font-serif)"
        }}>
            A{" "}
            <span className="relative inline-block italic font-bold" style={{
            fontVariationSettings: '"ital" 1'
          }}>
              partnership
              <span aria-hidden="true" className="absolute -bottom-1.5 left-0 flex w-full items-center">
                <span className="h-0.5 flex-1 bg-accent" />
                <span className="ml-1 block h-1.5 w-1.5 rounded-full bg-accent" />
              </span>
            </span>
            , not a service.
          </h2>

          <p className="mx-auto mt-6 max-w-[680px] text-base leading-relaxed text-muted-foreground md:text-lg">
            We function as an extension of your agency, providing the precision tools and human expertise required to scale your insurance business sustainably.
          </p>
        </div>

        {/* Content Grid */}
        <div className="mt-16 grid grid-cols-1 items-start gap-8 md:mt-20 md:grid-cols-12 md:gap-8 lg:gap-12">
          {/* Featured Image Panel */}
          <div ref={imageRef} className={`transition-all duration-[640ms] ease-[cubic-bezier(0.22,1,0.36,1)] md:col-span-5 lg:col-span-5 ${imageVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
            <div className="overflow-hidden rounded-xl border border-border bg-card p-3.5 shadow-lg">
              <div className="relative overflow-hidden rounded-lg bg-secondary" style={{
              aspectRatio: "4 / 5"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/insurance_growth_metrics.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/insurance_growth_metrics.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/insurance_growth_metrics.png-wvc-sizes" alt="Professional insurance growth analytics and strategy dashboard" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-105" />
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60" />
              </div>

              <div className="mt-3.5 flex items-center justify-between gap-4 px-1 pb-0.5">
                <p className="font-mono text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  PLATE V — GROWTH ARCHITECTURE
                </p>
                <p className="font-mono text-[0.6875rem] font-bold uppercase tracking-[0.14em] text-accent">
                  48.8566° N / 2.3522° E
                </p>
              </div>
            </div>
          </div>

          {/* Ritual Items List */}
          <ul ref={listRef} className={`flex flex-col gap-4 transition-all duration-[640ms] ease-[cubic-bezier(0.22,1,0.36,1)] md:col-span-7 lg:col-span-7 ${listVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
            {rituals.map((ritual, i) => {
            const Icon = ritual.icon;
            return <li key={i} data-index={i} className="group relative rounded-xl border border-border bg-card p-6 shadow-xs transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-md md:p-6">
                  <div className="flex items-start gap-5">
                    {/* Icon Container with Gold & Navy Accents */}
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary shadow-2xs transition-all duration-300 group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground group-hover:shadow-sm">
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </div>

                    <div className="flex-1">
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                        <h3 className="text-base font-bold tracking-tight text-foreground md:text-lg">
                          {ritual.title}
                        </h3>
                        <p className="font-mono text-[0.6875rem] font-bold uppercase tracking-[0.12em] text-accent">
                          {ritual.caption}
                        </p>
                      </div>

                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground md:text-[0.9375rem]">
                        {ritual.description}
                      </p>
                    </div>
                  </div>
                </li>;
          })}
          </ul>
        </div>

        {/* BBB Accreditation Bar */}
        <div className="mt-16 flex flex-col items-center justify-between gap-6 rounded-xl border border-border bg-card p-6 shadow-sm sm:flex-row md:mt-20 md:p-8">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
              <ShieldCheck className="h-6 w-6 text-primary" strokeWidth={1.75} />
            </div>
            <p className="font-default text-sm font-semibold tracking-tight text-foreground md:text-base">
              We are Certified with BBB Best Business Bureau Michigan
            </p>
          </div>
          <img src="https://rmasolutionsllc.net/wp-content/uploads/2026/06/bbb-90071778-3.png" alt="BBB Accredited Business Seal" className="h-10 w-auto shrink-0 object-contain brightness-95 contrast-125" loading="lazy" decoding="async" />
        </div>
      </div>
    </section>;
}